import * as React from 'react';
import { isDefined } from '@nfctron/nexus-core';
import { Slot, Slottable } from '@radix-ui/react-slot';
import { type VariantProps } from 'tailwind-variants';

import { type ExtendedComponentProps } from '../types';
import { cn, tv } from '../utils';

/* -------------------------------------------------------------------------------------------------
 * FeatureIcon
 * -----------------------------------------------------------------------------------------------*/

const featureIconVariants = tv({
	base: [
		'rounded-full flex flex-col items-center justify-center',
		'[&>div]:rounded-full [&>div]:flex [&>div]:items-center [&>div]:justify-center [&>div]:w-full [&>div]:h-full',
	],
	variants: {
		variant: {
			subtle: '',
			unstyled: '',
		},
		color: {
			purple: '',
			zinc: '',
			unstyled: '',
		},
		size: {
			default: 'size-16 p-2.5',
			unstyled: '',
		},
	},
	compoundVariants: [
		{
			variant: 'subtle',
			color: 'zinc',
			className: cn('bg-zinc-50 [&>div]:bg-zinc-100 text-zinc-800', 'dark:bg-zinc-900 dark:[&>div]:bg-zinc-800 dark:text-zinc-200'),
		},
	],
	defaultVariants: {
		color: 'zinc',
		variant: 'subtle',
		size: 'default',
	},
});

export interface UiFeatureIconProps extends ExtendedComponentProps<VariantProps<typeof featureIconVariants>, 'div'> {
	asChild?: boolean;
	icon?: React.ReactNode;
}

const UiFeatureIcon = React.forwardRef<HTMLDivElement, UiFeatureIconProps>((props, ref) => {
	const { children, className, variant, size, color, asChild = false, icon, ...rest } = props;
	const Comp = asChild ? Slot : 'div';
	return (
		<Comp className={cn(featureIconVariants({ variant, size, className, color }))} ref={ref} {...rest}>
			<div>
				<Slottable>{isDefined(icon) ? <Slot className="size-6">{icon}</Slot> : children}</Slottable>
			</div>
		</Comp>
	);
});
UiFeatureIcon.displayName = 'ui/Button';

/* -----------------------------------------------------------------------------------------------*/

export { UiFeatureIcon, featureIconVariants };
